import { clsx } from 'clsx'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react'
import { ChevronDown, Check } from 'lucide-react'
import type { Category } from '@/lib/types'
import { getCategoryColorStyle } from '@/lib/categoryColors'
import { Fragment } from 'react'

interface CategoryTabsProps {
  categories: Category[]
  activeCategoryId: number | null
  onSelect: (id: number | null) => void
}

export function CategoryTabs({ categories, activeCategoryId, onSelect }: CategoryTabsProps) {
  const sortedCategories = categories
    .filter(c => !c.parentId)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .flatMap(root => {
      const children = categories
        .filter(c => c.parentId === root.id)
        .sort((a, b) => a.sortOrder - b.sortOrder)
      return [root, ...children]
    })

  const selectedCategory = activeCategoryId !== null
    ? categories.find(c => c.id === activeCategoryId)
    : null

  const displayLabel = selectedCategory
    ? `${selectedCategory.icon ? selectedCategory.icon + ' ' : ''}${selectedCategory.name}`
    : '카테고리'

  return (
    <Listbox value={activeCategoryId} onChange={onSelect}>
      <div className="relative">
        <ListboxButton
          aria-label={`카테고리 선택, 현재 ${displayLabel}`}
          className={clsx(
            'workspace-primary-action flex min-h-[44px] items-center gap-2 rounded-xl px-4 text-sm font-semibold',
            'transition-all whitespace-nowrap text-white',
            'active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
          )}
        >
          <span className="truncate max-w-[160px]">{displayLabel}</span>
          <ChevronDown className="w-4 h-4 flex-shrink-0 ui-open:rotate-180 transition-transform duration-200" />
        </ListboxButton>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <ListboxOptions
            anchor={{ to: 'bottom start', gap: '8px', padding: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)' }}
            className={clsx(
              'w-56 z-[60]',
              'max-h-60 overflow-auto overscroll-contain',
              'rounded-2xl bg-[var(--workspace-panel-strong)]',
              'shadow-e3 border border-[var(--workspace-border-strong)] backdrop-blur-xl',
              'p-1.5 focus:outline-none'
            )}
          >
            {/* "All" option */}
            <ListboxOption
              value={null}
              className={({ focus, selected }) => clsx(
                'relative flex min-h-[44px] cursor-pointer select-none items-center rounded-xl pl-10 pr-4 text-sm',
                focus ? 'bg-accent-primary text-accent-primary' : 'text-heading',
                selected && 'font-semibold'
              )}
            >
              {({ selected }) => (
                <>
                  <span className="block truncate">전체 카테고리</span>
                  {selected && (
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-500">
                      <Check className="w-4 h-4" />
                    </span>
                  )}
                </>
              )}
            </ListboxOption>

            {/* Divider */}
            <div className="mx-2 my-1 border-t border-base" />

            {/* Category options */}
            {sortedCategories.map((category) => (
              <ListboxOption
                key={category.id}
                value={category.id ?? null}
                className={({ focus, selected }) => clsx(
                  'relative flex min-h-[44px] cursor-pointer select-none items-center rounded-xl pr-4 text-sm',
                  category.parentId ? 'pl-14' : 'pl-10',
                  focus ? 'bg-accent-primary text-accent-primary' : 'text-heading',
                  selected && 'font-semibold'
                )}
              >
                {({ selected }) => (
                  <>
                    <span className="flex items-center gap-2 truncate">
                      {category.parentId && (
                        <span className="text-disabled text-xs">└</span>
                      )}
                      {category.color && (
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: getCategoryColorStyle(category.color) }}
                        />
                      )}
                      {category.icon && <span>{category.icon}</span>}
                      {category.name}
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-500">
                        <Check className="w-4 h-4" />
                      </span>
                    )}
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Transition>
      </div>
    </Listbox>
  )
}
